import type { QueryClient, QueryKey } from '@tanstack/react-query';

/** Event types emitted by `GET /v1/stream` (07 §3). */
export const STREAM_EVENT_TYPES = [
  'run.updated',
  'conversation.message',
  'review.created',
  'escalation.created',
  'deployment.changed',
  'proposal.ready',
] as const;
export type StreamEventType = (typeof STREAM_EVENT_TYPES)[number];

export interface StreamEvent {
  type: string;
  tenantId?: string | null;
  resourceId: string;
  data?: unknown;
  at?: string;
}

export type StreamStatus = 'connecting' | 'open' | 'closed';

/** The subset of the browser EventSource the client needs (injectable for tests). */
export interface EventSourceLike {
  onopen: ((this: EventSourceLike, ev: Event) => unknown) | null;
  onerror: ((this: EventSourceLike, ev: Event) => unknown) | null;
  addEventListener(type: string, listener: (ev: MessageEvent<string>) => void): void;
  close(): void;
}

export interface EventStreamOptions {
  /** Builds the stream URL with a fresh token on every (re)connect; null = not signed in. */
  url: () => string | null;
  onEvent: (event: StreamEvent) => void;
  onStatus?: (status: StreamStatus) => void;
  createSource?: (url: string) => EventSourceLike;
  reconnectBaseMs?: number;
  reconnectMaxMs?: number;
}

/**
 * SSE client with exponential-backoff reconnect. The server sends named events (`event: run.updated`), so a
 * listener is registered per known type. Errors close the source and reconnect with a fresh URL, because the
 * access token in the query string may have been rotated in the meantime.
 */
export class EventStreamClient {
  private source: EventSourceLike | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private stopped = true;
  private readonly opts: Required<Pick<EventStreamOptions, 'reconnectBaseMs' | 'reconnectMaxMs'>> & EventStreamOptions;

  constructor(opts: EventStreamOptions) {
    this.opts = { reconnectBaseMs: 1000, reconnectMaxMs: 30000, ...opts };
  }

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.source?.close();
    this.source = null;
    this.opts.onStatus?.('closed');
  }

  /** Delay before the next reconnect attempt (exposed for tests). */
  nextDelay(): number {
    return Math.min(this.opts.reconnectMaxMs, this.opts.reconnectBaseMs * 2 ** Math.min(this.attempt, 10));
  }

  private connect(): void {
    if (this.stopped) return;
    const url = this.opts.url();
    if (!url) {
      this.opts.onStatus?.('closed');
      return;
    }
    const create = this.opts.createSource ?? ((u: string) => new EventSource(u) as unknown as EventSourceLike);
    this.opts.onStatus?.('connecting');
    const source = create(url);
    this.source = source;
    source.onopen = () => {
      this.attempt = 0;
      this.opts.onStatus?.('open');
    };
    source.onerror = () => {
      source.close();
      if (this.source !== source || this.stopped) return;
      this.source = null;
      this.opts.onStatus?.('closed');
      const delay = this.nextDelay();
      this.attempt++;
      this.timer = setTimeout(() => this.connect(), delay);
    };
    for (const type of STREAM_EVENT_TYPES) {
      source.addEventListener(type, (ev) => {
        const parsed = parseEvent(type, ev.data);
        if (parsed) this.opts.onEvent(parsed);
      });
    }
  }
}

function parseEvent(type: string, data: string): StreamEvent | null {
  try {
    const v = JSON.parse(data) as Partial<StreamEvent>;
    return { type: v.type ?? type, tenantId: v.tenantId ?? null, resourceId: v.resourceId ?? '', data: v.data, at: v.at };
  } catch {
    return null;
  }
}

/** Builds the SSE URL: EventSource cannot send headers, so the token and tenant scope travel as query params. */
export function buildStreamUrl(baseUrl: string, accessToken: string | null, tenantId: string | null): string | null {
  if (!accessToken) return null;
  const q = new URLSearchParams({ access_token: accessToken });
  if (tenantId) q.set('tenantId', tenantId);
  return `${baseUrl}/v1/stream?${q.toString()}`;
}

/** Query areas (third segment of `queryKeys.scoped`) refreshed by each event type. */
export const INVALIDATIONS: Record<string, readonly string[]> = {
  'run.updated': ['runs', 'metrics', 'reviews'],
  'conversation.message': ['conversations'],
  'review.created': ['reviews', 'metrics'],
  'escalation.created': ['escalations', 'metrics'],
  'deployment.changed': ['deployments', 'agents'],
  'proposal.ready': ['proposals'],
};

/** Areas re-fetched periodically while the stream is down (polling fallback, 07 §3). */
export const POLL_AREAS: readonly string[] = ['runs', 'conversations', 'reviews', 'escalations', 'metrics'];

export function keyInAreas(key: QueryKey, areas: readonly string[]): boolean {
  return key[0] === 'tenant' && typeof key[2] === 'string' && areas.includes(key[2]);
}

export function invalidateAreas(qc: QueryClient, areas: readonly string[]): Promise<void> {
  return qc.invalidateQueries({ predicate: (q) => keyInAreas(q.queryKey, areas) });
}

export function invalidateForEvent(qc: QueryClient, event: Pick<StreamEvent, 'type'>): Promise<void> {
  const areas = INVALIDATIONS[event.type];
  return areas ? invalidateAreas(qc, areas) : Promise.resolve();
}
